import { isNil } from 'lodash'
import { CoolingGroundHeatPumpParameters } from '../../generated/graphql'
import { EnergyType, Quadrant } from '../EnergyFlow'
import {
  createHourlyProvided,
  createHourlyRequirement,
  createSecondaryHourlyWaste,
  HourlyEnergyFlow,
  PrimaryHourlyEnergyFlow,
} from '../HourlyEnergyFlow'
import {
  SingleTypeHourlyVariables,
  SingleTypeProvider,
} from './common/SingleTypeProvider'

type NonFreeCoolingParameters = {
  freeCooling: false
  eer: number
  maxPower: number
}

type FreeCoolingParameters = Omit<NonFreeCoolingParameters, 'freeCooling'> & {
  freeCooling: true
  freeCoolingEer: number
  freeCoolingMaxPower: number
}

/**
 * If free cooling is active, first provides free cooling with a limited power and small electricity consumption.
 * For the remaining demand, provides active cooling with a limited power and larger electricity consumption.
 */
export class CoolingGroundHeatPumpProvider extends SingleTypeProvider<CoolingGroundHeatPumpParameters> {
  public readonly providedEnergyTypes = [EnergyType.Cooling]

  private validatedParameters!: FreeCoolingParameters | NonFreeCoolingParameters

  protected initialize(): void {
    const { eer, maxPower, freeCooling, freeCoolingEer, freeCoolingMaxPower } =
      this.parameters

    if (isNil(maxPower)) {
      throw new Error('Max power must be defined')
    }

    if (!freeCooling) {
      this.validatedParameters = { eer, maxPower, freeCooling }
      return
    }

    if (isNil(freeCoolingEer) || isNil(freeCoolingMaxPower)) {
      throw new Error('Free cooling parameters must be defined')
    }

    this.validatedParameters = {
      eer,
      maxPower,
      freeCooling,
      freeCoolingEer,
      freeCoolingMaxPower,
    }
  }

  protected provideForHour({
    remainingDemand,
  }: SingleTypeHourlyVariables): HourlyEnergyFlow[] {
    const { eer, maxPower } = this.validatedParameters

    if (this.validatedParameters.freeCooling) {
      const { freeCoolingEer, freeCoolingMaxPower } = this.validatedParameters

      const flowsByFreeCooling = this.provideUpToMaxPower(
        freeCoolingEer,
        freeCoolingMaxPower,
        remainingDemand,
      )
      const providedByFreeCooling = flowsByFreeCooling.find(
        (flow) => flow.quadrant === Quadrant.Provided,
      )

      if (!providedByFreeCooling) {
        throw new Error(
          `Error in provider ${this.id} while providing for demand ${remainingDemand.demandId}.`,
        )
      }

      const remainingAfterFreeCooling = {
        ...remainingDemand,
        value: remainingDemand.value - providedByFreeCooling.value,
      }

      const flowsByActiveCooling = this.provideUpToMaxPower(
        eer,
        maxPower,
        remainingAfterFreeCooling,
      )

      const getActiveValueFromMatchingFlow = (freeFlow: HourlyEnergyFlow) => {
        const activeFlow = flowsByActiveCooling.find(
          (activeCoolingFlow) =>
            activeCoolingFlow.energyType === freeFlow.energyType &&
            activeCoolingFlow.quadrant === freeFlow.quadrant,
        )

        if (!activeFlow) {
          throw new Error(
            `Error in provider ${this.id} while providing for demand ${remainingDemand.demandId}.`,
          )
        }

        return activeFlow.value
      }

      return flowsByFreeCooling.map((freeCoolingFlow) => ({
        ...freeCoolingFlow,
        value:
          freeCoolingFlow.value +
          getActiveValueFromMatchingFlow(freeCoolingFlow),
      }))
    }

    return this.provideUpToMaxPower(eer, maxPower, remainingDemand)
  }

  private provideUpToMaxPower(
    eer: number,
    maxPower: number,
    remainingDemand: PrimaryHourlyEnergyFlow,
  ): HourlyEnergyFlow[] {
    const provided = createHourlyProvided(
      remainingDemand.demandId,
      remainingDemand.energyType,
      Math.min(remainingDemand.value, maxPower),
    )

    const requirement = createHourlyRequirement(
      provided.demandId,
      EnergyType.Electricity,
      provided.value / eer,
    )

    const waste = createSecondaryHourlyWaste(
      provided.demandId,
      EnergyType.Heating,
      provided.value + requirement.value,
    )

    return [provided, waste, requirement]
  }
}
